/**
 * Generic DataDashboard Component
 * 
 * A reusable dashboard component that provides a consistent layout and functionality
 * for displaying tabular data across all admin dashboard pages. This component
 * implements the "Same Layout, Different Data" pattern, allowing rapid development
 * of new dashboard pages with minimal code duplication.
 * 
 * ## Key Features
 * - **Generic table structure**: Handles any data type with column definitions
 * - **Search functionality**: Full-text search across all columns
 * - **Export capabilities**: Excel/CSV export with field selection
 * - **Header actions**: Edit, Save, Export, Delete, Show Count buttons
 * - **Selection functionality**: Multi-select with checkboxes
 * - **Inline editing**: Edit data directly in table cells
 * - **Responsive design**: Mobile-friendly layout
 * - **Loading states**: Proper loading and empty state handling
 * 
 * ## Usage Patterns
 * 
 * ### Simple Dashboard
 * ```typescript
 * <DataDashboard
 *   title="Products"
 *   collection="products"
 *   data={products}
 *   columns={productColumns}
 *   user={user}
 *   currentRoute={currentRoute}
 *   onNavigate={onNavigate}
 *   onLogout={onLogout}
 *   onDataChange={setProducts}
 * />
 * ```
 * 
 * ### Full-Featured Dashboard
 * ```typescript
 * <DataDashboard
 *   title="Companies"
 *   collection="companies"
 *   data={companies}
 *   columns={companyColumns}
 *   user={user}
 *   currentRoute={currentRoute}
 *   onNavigate={onNavigate}
 *   onLogout={onLogout}
 *   onDataChange={setCompanies}
 *   onAdd={handleAddCompany}
 *   onEdit={handleEditCompany}
 *   onDelete={handleDeleteCompanies}
 *   onExport={handleExport}
 *   AddModal={AddCompanyModal}
 *   customHeaderActions={<CustomActions />}
 *   showAddButton={true}
 *   showSearch={true}
 *   showSelection={true}
 *   pageDescription="Manage company information and user codes"
 * />
 * ```
 * 
 * ## Data Flow
 * 1. Component receives data array and column definitions
 * 2. Data is filtered by search term and sorted by user selection
 * 3. Table renders with proper column types and custom renderers
 * 4. User interactions (edit, delete, export) trigger appropriate handlers
 * 5. Local state updates immediately, API calls handle persistence
 * 
 * ## Customization Points
 * - **Column definitions**: Control how data is displayed
 * - **Custom renderers**: Override default cell rendering
 * - **Header actions**: Add custom buttons to header
 * - **Add modal**: Provide custom modal for adding new items
 * - **Event handlers**: Handle edit, delete, export operations
 * 
 * @component
 * @example
 * // Basic usage
 * <DataDashboard
 *   title="Users"
 *   collection="users"
 *   data={users}
 *   columns={userColumns}
 *   user={adminUser}
 *   currentRoute="/dashboard/users"
 *   onNavigate={handleNavigate}
 *   onLogout={handleLogout}
 *   onDataChange={setUsers}
 * />
 * 
 * @example
 * // Advanced usage with custom features
 * <DataDashboard
 *   title="Orders"
 *   collection="orders"
 *   data={orders}
 *   columns={orderColumns}
 *   user={adminUser}
 *   currentRoute="/dashboard/orders"
 *   onNavigate={handleNavigate}
 *   onLogout={handleLogout}
 *   onDataChange={setOrders}
 *   onEdit={handleEditOrder}
 *   onDelete={handleDeleteOrders}
 *   AddModal={AddOrderModal}
 *   customHeaderActions={<BulkActions />}
 *   showAddButton={true}
 *   showSearch={true}
 *   showSelection={true}
 *   pageDescription="Manage customer orders and fulfillment"
 * />
 */

import React, { useState, useEffect, useRef, useMemo, memo } from 'react';
import AdminLayout from '../../admin-v2/layout/AdminLayout';
import UserLayout from '../../user-v2/layout/UserLayout';
import HeaderActions, { ActionButton } from '../HeaderActions';
import { createStandardHeaderActions } from '../HeaderActions/helpers';
import ExportModal from '../ExportModal';
import CategorySelector from '../CategorySelector';
import CompanySelector from '../CompanySelector';
import { AdminUser } from '../../../types/admin-v2';
import { API_ENDPOINTS, config } from '../../../utils/config';
import AddSupplierModal from '../../admin-v2/modals/AddSupplierModal';
import { getCategoryName, type Language } from '../../../utils/categories';
import './DataDashboard.scss';

/**
 * Column definition interface for DataDashboard table
 * 
 * Defines how each column should be displayed and behave in the data table.
 * Supports various column types with custom rendering capabilities.
 * 
 * @interface Column
 */
interface Column {
  /** Field name in the data object */
  key: string;
  /** Column header text displayed in table */
  title: string;
  /** Column type determines default rendering behavior */
  type: 'text' | 'number' | 'date' | 'status' | 'action' | 'score' | 'price_range' | 'image' | 'categories' | 'companies' | 'dropdown';
  /** Whether column can be sorted by clicking header */
  sortable?: boolean;
  /** Whether column can be filtered (future feature) */
  filterable?: boolean;
  /** CSS width for the column (e.g., '100px', '20%') */
  width?: string;
  /** Whether column cells can be edited inline */
  editable?: boolean;
  /** Custom render function for complex data display */
  render?: (value: any, row: any) => React.ReactNode;
  /** Dropdown options for editable select fields - can be string[] for dropdown type or {value, label}[] for other types */
  options?: Array<{ value: string; label: string }> | string[];
  /** Function to get display value from stored value */
  getDisplayValue?: (value: string) => string;
  /** Whether the column should be hidden by default */
  defaultHidden?: boolean;
}

/**
 * Props interface for DataDashboard component
 * 
 * Defines all configuration options and event handlers for the generic dashboard.
 * Most props are optional with sensible defaults, allowing for simple usage
 * while providing extensive customization options.
 * 
 * @interface DataDashboardProps
 */
interface DataDashboardProps {
  /** Page title displayed in header and browser tab */
  title: string;
  /** Backend collection name used for export functionality */
  collection: string;
  /** Array of data objects to display in table */
  data: any[];
  /** Column definitions controlling table structure and behavior */
  columns: Column[];
  /** Loading state - shows spinner while true */
  isLoading?: boolean;
  /** Error message to display if data loading fails */
  error?: string | null;
  /** Current authenticated user object */
  user: AdminUser;
  /** Current route path for navigation context */
  currentRoute: string;
  /** Function to handle navigation to different routes */
  onNavigate: (route: string) => void;
  /** Function to handle user logout */
  onLogout: () => void;
  /** Function called when data array changes (for state updates) */
  onDataChange: (newData: any[]) => void;
  /** Optional function to handle adding new items */
  onAdd?: () => void;
  /** Optional function to handle editing individual items */
  onEdit?: (id: string, field: string, value: any, row?: any) => void;
  /** Optional function to handle edit mode toggle */
  onEditToggle?: () => void;
  /** Optional function to handle deleting multiple items */
  onDelete?: (ids: string[]) => void;
  /** Optional custom export handler (uses default ExportModal if not provided) */
  onExport?: (exportData: any) => void;
  /** Optional React component for adding new items */
  AddModal?: React.ComponentType<any>;
  /** Optional custom header actions to replace default buttons */
  customHeaderActions?: React.ReactNode;
  /** Optional header actions array for buttons like cart, etc. */
  headerActions?: Array<ActionButton & {
    key: string;
    className?: string;
  }>;
  /** Optional navigation actions (Back/Next buttons) to show below header */
  navigationActions?: React.ReactNode;
  /** Whether to show "Add New" button (default: true) */
  showAddButton?: boolean;
  /** Custom label for Add New button */
  addButtonLabel?: string;
  /** Whether to show search input field (default: true) */
  showSearch?: boolean;
  /** Whether to show selection checkboxes (default: true) */
  showSelection?: boolean;
  /** Whether to show "Edit" button in header actions (default: true) */
  showEditButton?: boolean;
  /** Whether to show "Delete" button in header actions (default: true) */
  showDeleteButton?: boolean;
  /** Whether to show "Export" button in header actions (default: true) */
  showExportButton?: boolean;
  /** Optional row action function for each row (like "Add to Cart") */
  onRowAction?: (item: any) => void;
  /** Label for row action button */
  rowActionLabel?: string;
  /** Cart button props for user layout */
  cartButton?: {
    label: string;
    onClick: () => void;
    className?: string;
  };
  /** Optional cart item count for sidebar badge */
  cartItemCount?: number;
  /** Optional page description for header subtitle */
  pageDescription?: string;
  /** Optional custom export fields for ExportModal */
  customExportFields?: Array<{ name: string; type: string; description: string }>;
  /** Layout type - 'admin' uses AdminLayout, 'user' uses UserLayout, undefined/null means no layout wrapper */
  layoutType?: 'admin' | 'user' | null;
  /** Skip layout wrapper - if true, only render content without AdminLayout/UserLayout wrapper */
  noLayout?: boolean;
  /** Whether dashboard is in editing mode */
  isEditing?: boolean;
  /** Function to save pending changes */
  onSaveChanges?: () => void;
  /** Currently selected items for bulk operations */
  selectedItems?: string[];
  /** Number of selected items */
  selectedItemsCount?: number;
  /** Function to handle selection changes */
  onSelectionChange?: (selectedIds: string[]) => void;
  /** Current items per page setting */
  itemsPerPage?: number;
  /** Function to handle items per page changes */
  onItemsPerPageChange?: (itemsPerPage: number) => void;
  /** Optional CSS class name for styling */
  className?: string;
  /** Optional function to handle backend data fetching with pagination, search, sort */
  onDataFetch?: (params: { page: number; limit: number; search?: string; sortBy?: string; sortOrder?: string; columnFilters?: Record<string, string> }) => void;
  /** Optional function to handle opening item detail (receives selected item ID) */
  onOpen?: (itemId: string) => void;
  /** Optional function to handle row click (receives full item object) */
  onRowClick?: (item: any) => void;
  /** Optional function to get custom className for a row */
  getRowClassName?: (row: any) => string;
  /** Current pagination info from backend */
  paginationInfo?: {
    page: number;
    limit: number;
    total: number;
    pages: number;
    has_next: boolean;
    has_prev: boolean;
  };
  /** Optional content to display between header and dashboard table */
  headerContent?: React.ReactNode;
  /** Optional secondary sidebar (only used in admin layout) */
  secondarySidebar?: React.ReactNode;
  /** Dynamic sub-items function for sidebar navigation */
  getDynamicSubItems?: (parentId: string, currentRoute: string) => Array<{
    id: string;
    label: string;
    route: string;
    icon?: string;
  }> | undefined;
  /** Whether rows are expandable (shows expand/collapse icon) */
  expandableRows?: boolean;
  /** Set of expanded row IDs */
  expandedRows?: Set<string>;
  /** Callback when row expand/collapse is clicked */
  onRowExpand?: (rowId: string) => void;
  /** Function to render expanded content below row */
  renderExpandedContent?: (row: any) => React.ReactNode;
  onFiltersToggleStateChange?: (isOpen: boolean) => void;
  /** External filters toggle state */
  externalFiltersToggleState?: boolean;
  /** Whether to hide the default expand column (use when implementing custom expand toggles) */
  hideExpandColumn?: boolean;
}

const DataDashboard: React.FC<DataDashboardProps> = ({
  title,
  collection,
  data,
  columns,
  isLoading = false,
  error,
  user,
  currentRoute,
  onNavigate,
  onLogout,
  onDataChange,
  onAdd,
  onEdit,
  onEditToggle: propOnEditToggle,
  onDelete,
  onExport,
  AddModal,
  customHeaderActions,
  headerActions: propHeaderActions,
  navigationActions,
  showAddButton = true,
  addButtonLabel,
  showSearch = true,
  showSelection = true,
  showEditButton = true,
  showDeleteButton = true,
  showExportButton = true,
  onRowAction,
  rowActionLabel,
  cartButton,
  cartItemCount = 0,
  pageDescription,
  customExportFields,
  layoutType = 'admin',
  noLayout = false,
  className,
  onDataFetch,
  onOpen,
  onRowClick,
  getRowClassName,
  paginationInfo,
  isEditing: propIsEditing,
  onSaveChanges: propOnSaveChanges,
  selectedItems: propSelectedItems,
  selectedItemsCount: propSelectedItemsCount,
  onSelectionChange: propOnSelectionChange,
  itemsPerPage: propItemsPerPage,
  onItemsPerPageChange: propOnItemsPerPageChange,
  headerContent,
  secondarySidebar,
  getDynamicSubItems,
  expandableRows = false,
  expandedRows,
  onRowExpand,
  renderExpandedContent,
  onFiltersToggleStateChange,
  externalFiltersToggleState,
  hideExpandColumn = false
}) => {
  // State management
  // Use prop selectedItems if provided, otherwise use local state
  const [localSelectedItems, setLocalSelectedItems] = useState<string[]>([]);
  const selectedItems = propSelectedItems !== undefined ? propSelectedItems : localSelectedItems;
  const setSelectedItems = propSelectedItems !== undefined
    ? (propOnSelectionChange || (() => { }))
    : setLocalSelectedItems;

  const [selectedItemsCount, setSelectedItemsCount] = useState<number>(propSelectedItemsCount || selectedItems.length);
  // Use prop isEditing directly, no local state
  const isEditing = propIsEditing || false;

  const [pendingChanges, setPendingChanges] = useState<Record<string, any>>({});
  const [searchTerm, setSearchTerm] = useState('');
  const [columnFilters, setColumnFilters] = useState<Record<string, string>>({});
  const [debounceTimeouts, setDebounceTimeouts] = useState<Record<string, NodeJS.Timeout>>({});
  const [showAddModal, setShowAddModal] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [sortConfig, setSortConfig] = useState<{ key: string; direction: 'asc' | 'desc' } | null>(null);
  const [sortLoading, setSortLoading] = useState<boolean>(false);
  const [itemsPerPage, setItemsPerPage] = useState<number>(propItemsPerPage || paginationInfo?.limit || 25);
  const [draggedColumn, setDraggedColumn] = useState<string | null>(null);
  const [columnOrder, setColumnOrder] = useState<string[]>([]);
  const [columnVisibility, setColumnVisibility] = useState<Record<string, boolean>>({});
  const [showColumnToggle, setShowColumnToggle] = useState(externalFiltersToggleState || false);

  // Sync external filters toggle state
  useEffect(() => {
    if (externalFiltersToggleState !== undefined) {
      setShowColumnToggle(externalFiltersToggleState);
    }
  }, [externalFiltersToggleState]);

  // Notify parent of filters toggle state changes
  useEffect(() => {
    if (onFiltersToggleStateChange) {
      onFiltersToggleStateChange(showColumnToggle);
    }
  }, [showColumnToggle, onFiltersToggleStateChange]);
  const [columnWidths, setColumnWidths] = useState<Record<string, number>>({});
  const [resizingColumn, setResizingColumn] = useState<string | null>(null);
  const [resizeStartX, setResizeStartX] = useState<number>(0);
  const [resizeStartWidth, setResizeStartWidth] = useState<number>(0);

  useEffect(() => {
    if (propSelectedItems) {
      const next = propSelectedItems;
      if (propOnSelectionChange) {
        // If parent controls selection, just sync the count
        const nextCount = propSelectedItemsCount !== undefined
          ? propSelectedItemsCount
          : propSelectedItems.length;
        setSelectedItemsCount(nextCount);
      } else {
        // Update local state if not controlled
        setLocalSelectedItems((prev: string[]) => {
          const isSameLength = prev.length === next.length;
          const hasSameItems = isSameLength && prev.every((value: string, index: number) => value === next[index]);
          return hasSameItems ? prev : next;
        });
        const nextCount = propSelectedItemsCount !== undefined
          ? propSelectedItemsCount
          : propSelectedItems.length;
        setSelectedItemsCount(nextCount);
      }
    } else if (propSelectedItemsCount !== undefined) {
      setSelectedItemsCount(propSelectedItemsCount);
    }
  }, [propSelectedItems, propSelectedItemsCount, propOnSelectionChange]);

  // Track previous data to detect actual changes
  const prevDataRef = useRef<any[]>(data);

  // Initialize column order and visibility from props or localStorage
  useEffect(() => {
    if (columns && columns.length > 0) {
      const storageKey = `column_order_${collection}`;
      const visibilityKey = `column_visibility_${collection}`;
      const widthsKey = `column_widths_${collection}`;
      const orderVersionKey = `column_order_version_${collection}`;

      // Generate a version hash from the default column order (first 5 keys)
      const defaultOrderHash = columns.slice(0, 5).map(col => col.key).join('|');

      // Load from localStorage or use default
      const savedOrder = localStorage.getItem(storageKey);
      const savedVisibility = localStorage.getItem(visibilityKey);
      const savedWidths = localStorage.getItem(widthsKey);
      const savedOrderVersion = localStorage.getItem(orderVersionKey);

      // Check if the default column order has changed (developer reordered columns)
      const orderVersionChanged = savedOrderVersion !== defaultOrderHash;

      if (savedOrder && savedVisibility && !orderVersionChanged) {
        try {
          const parsedOrder = JSON.parse(savedOrder);
          const parsedVisibility = JSON.parse(savedVisibility);
          const parsedWidths = savedWidths ? JSON.parse(savedWidths) : {};

          // Validate that all columns still exist
          const validOrder = parsedOrder.filter((key: string) =>
            columns.some(col => col.key === key)
          );
          const validVisibility = parsedVisibility;

          // Add any new columns that weren't in localStorage
          columns.forEach(col => {
            if (!validOrder.includes(col.key)) {
              validOrder.push(col.key);
            }
            if (!(col.key in validVisibility)) {
              validVisibility[col.key] = col.defaultHidden ? false : true;
            }
          });

          setColumnOrder(validOrder);
          setColumnVisibility(validVisibility);
          setColumnWidths(parsedWidths);
        } catch (error) {
          console.error('Error parsing column settings:', error);
          // Fallback to default
          setColumnOrder(columns.map(col => col.key));
          setColumnVisibility(columns.reduce((acc, col) => ({ ...acc, [col.key]: true }), {}));
          setColumnWidths({});
          localStorage.setItem(orderVersionKey, defaultOrderHash);
        }
      } else {
        // Default values (no saved order, or order version changed)
        if (orderVersionChanged) {
          console.log(`Column order version changed for ${collection}, resetting to default`);
          // Clear old order from localStorage since it's stale
          localStorage.removeItem(storageKey);
        }
        setColumnOrder(columns.map(col => col.key));
        setColumnVisibility(columns.reduce((acc, col) => ({
          ...acc,
          [col.key]: col.defaultHidden ? false : true
        }), {}));
        setColumnWidths({});
        // Save the new version
        localStorage.setItem(orderVersionKey, defaultOrderHash);
      }
    }
  }, [columns, collection]);

  // Reset sort loading when data actually changes (backend response)
  useEffect(() => {
    if (sortLoading && data && data.length > 0) {
      // Check if data actually changed by comparing with previous data
      const dataChanged = prevDataRef.current !== data;

      console.log('📊 SORT DEBUG - Checking if should reset sort loading');
      console.log('📊 SORT DEBUG - Data changed:', dataChanged);
      console.log('📊 SORT DEBUG - Previous data length:', prevDataRef.current?.length);
      console.log('📊 SORT DEBUG - Current data length:', data.length);

      if (dataChanged) {
        console.log('📊 SORT DEBUG - Data actually changed, resetting sort loading');
        setSortLoading(false);
        prevDataRef.current = data;
        console.log('📊 SORT DEBUG - Sort loading reset to false');
      }
    }
  }, [data, sortLoading]);

  // Sync sort state with backend response
  useEffect(() => {
    if (onDataFetch && paginationInfo) {
      console.log('📊 SORT DEBUG - Pagination info updated');
      console.log('📊 SORT DEBUG - Current sortConfig:', sortConfig);

      // If we have pagination info, it means backend responded
      // Keep the current sort state or set default
      if (!sortConfig) {
        // Set default sort based on collection
        const defaultSortKey = collection === 'products' ? 'product_id' :
          collection === 'suppliers' ? 'global_id' :
            collection === 'companies' ? 'company_id' : 'name';
        setSortConfig({ key: defaultSortKey, direction: 'asc' });
      }
    }
  }, [paginationInfo, onDataFetch, sortConfig, collection]);

  // Save column order to localStorage when it changes
  useEffect(() => {
    if (columnOrder.length > 0 && collection) {
      const storageKey = `column_order_${collection}`;
      localStorage.setItem(storageKey, JSON.stringify(columnOrder));
    }
  }, [columnOrder, collection]);

  // Save column visibility to localStorage when it changes
  useEffect(() => {
    if (Object.keys(columnVisibility).length > 0 && collection) {
      const visibilityKey = `column_visibility_${collection}`;
      localStorage.setItem(visibilityKey, JSON.stringify(columnVisibility));
    }
  }, [columnVisibility, collection]);

  // Save column widths to localStorage when they change
  useEffect(() => {
    if (Object.keys(columnWidths).length > 0 && collection) {
      const widthsKey = `column_widths_${collection}`;
      localStorage.setItem(widthsKey, JSON.stringify(columnWidths));
    }
  }, [columnWidths, collection]);

  // Sync itemsPerPage with paginationInfo.limit
  useEffect(() => {
    if (paginationInfo?.limit && paginationInfo.limit !== itemsPerPage) {
      setItemsPerPage(paginationInfo.limit);
    }
  }, [paginationInfo?.limit]);

  // Sync itemsPerPage with propItemsPerPage
  useEffect(() => {
    if (propItemsPerPage && propItemsPerPage !== itemsPerPage) {
      setItemsPerPage(propItemsPerPage);
    }
  }, [propItemsPerPage]);

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      Object.values(debounceTimeouts).forEach(timeout => {
        if (timeout) clearTimeout(timeout);
      });
    };
  }, [debounceTimeouts]);

  // Handle items per page change
  const handleItemsPerPageChange = (newItemsPerPage: number) => {
    if (propOnItemsPerPageChange) {
      propOnItemsPerPageChange(newItemsPerPage);
    } else {
      setItemsPerPage(newItemsPerPage);
      if (onDataFetch) {
        // Reset to page 1 when changing items per page
        // Backend will return updated pagination info with correct pages calculation
        // Parent component will update paginationInfo from backend response
        onDataFetch({
          page: 1,
          limit: newItemsPerPage,
          search: searchTerm,
          sortBy: sortConfig?.key || 'name',
          sortOrder: sortConfig?.direction || 'asc',
          columnFilters: columnFilters
        });
      }
      // Note: If no onDataFetch, pagination is handled client-side
      // In that case, parent component should handle paginationInfo updates
    }
  };

  // For backend pagination, we use data directly. For client-side, we filter and sort.
  const displayData = useMemo(() => {
    // If backend pagination is enabled (onDataFetch provided), use data as-is
    if (onDataFetch) {
      return data;
    }

    let processedData = [...data];

    // 1. Client-side Column Filters
    if (Object.keys(columnFilters).length > 0) {
      processedData = processedData.filter(item =>
        Object.entries(columnFilters).every(([key, filterValue]) => {
          if (!filterValue) return true;
          const val = item[key];
          return String(val ?? '').toLowerCase().includes(filterValue.toLowerCase());
        })
      );
    }

    // 2. Client-side Search
    if (searchTerm) {
      const lowerTerm = searchTerm.toLowerCase();
      processedData = processedData.filter(item => {
        // Search only across defined columns to match what user sees
        return columns.some(col => {
          const val = item[col.key];
          // Handle custom render or simple value?
          // For search, we usually search raw values. 
          // If we want to search rendered values, we'd need to invoke render, but that's expensive/complex.
          // Searching raw values is standard.
          return String(val ?? '').toLowerCase().includes(lowerTerm);
        });
      });
    }

    // 2. Client-side Sort
    if (sortConfig) {
      processedData.sort((a, b) => {
        const aValue = a[sortConfig.key];
        const bValue = b[sortConfig.key];

        if (aValue === bValue) return 0;
        if (aValue === null || aValue === undefined) return 1;
        if (bValue === null || bValue === undefined) return -1;

        const compare = aValue < bValue ? -1 : 1;
        return sortConfig.direction === 'asc' ? compare : -compare;
      });
    }

    return processedData;
  }, [data, onDataFetch, searchTerm, sortConfig, columnFilters, columns]);

  // Handle item selection
  const handleSelectAll = () => {
    if (isEditing) {
      return;
    }
    const newSelectedItems = selectedItems.length === displayData.length
      ? []
      : displayData.map(item => item.id || item._id);

    setSelectedItems(newSelectedItems);
    setSelectedItemsCount(newSelectedItems.length);

    // Notify parent component
    if (propOnSelectionChange) {
      propOnSelectionChange(newSelectedItems);
    }
  };

  const handleSelectItem = (id: string) => {
    if (isEditing) {
      return;
    }
    const newSelectedItems = selectedItems.includes(id)
      ? selectedItems.filter(itemId => itemId !== id)
      : [...selectedItems, id];

    // Update local state if not controlled by parent
    if (propSelectedItems === undefined) {
      setLocalSelectedItems(newSelectedItems);
    }
    setSelectedItemsCount(newSelectedItems.length);

    // Notify parent component
    if (propOnSelectionChange) {
      propOnSelectionChange(newSelectedItems);
    }
  };

  // Handle edit mode
  const handleEditToggle = () => {
    if (propOnEditToggle) {
      propOnEditToggle();
    }
  };

  // Handle save changes
  const handleSaveChanges = () => {
    // Always process pending changes first
    if (Object.keys(pendingChanges).length > 0) {
      Object.entries(pendingChanges).forEach(([key, value]) => {
        // Split only on the first underscore to handle field names with underscores
        const firstUnderscoreIndex = key.indexOf('_');
        const itemId = key.substring(0, firstUnderscoreIndex);
        const field = key.substring(firstUnderscoreIndex + 1);
        console.log(`💾 SAVE DEBUG - itemId: ${itemId}, field: ${field}, value:`, value);

        // Find the row from displayData to pass it to onEdit
        const row = displayData.find(r => (r.id || r._id) === itemId);
        console.log(`💾 SAVE DEBUG - Found row:`, row);

        onEdit?.(itemId, field, value, row);
      });
      setPendingChanges({});
    }

    // Then call propOnSaveChanges if it exists (for additional cleanup)
    if (propOnSaveChanges) {
      propOnSaveChanges();
    }

    // Exit edit mode after saving
    if (propOnEditToggle) {
      propOnEditToggle();
    }
  };

  // Handle item edit
  const handleItemEdit = (id: string, field: string, value: any, row?: any) => {
    console.log(`📝 DataDashboard handleItemEdit - field: ${field}, value:`, value);
    if (isEditing) {
      setPendingChanges(prev => ({
        ...prev,
        [`${id}_${field}`]: value
      }));
    } else {
      // Pass the row to onEdit for company code handling
      onEdit?.(id, field, value, row);
    }
  };

  // Handle delete
  const handleDelete = () => {
    if (selectedItems.length > 0) {
      onDelete?.(selectedItems);
      setSelectedItems([]);
      setSelectedItemsCount(0);
    }
  };

  // Handle export
  const handleExport = async (exportData: any) => {
    console.log('🚀 DataDashboard handleExport called with:', exportData);

    if (onExport) {
      console.log('🚀 DataDashboard calling custom onExport handler');
      await onExport(exportData);
      return; // Custom handler will handle the export, don't continue with default
    } else {
      // Default export behavior - call ExportModal's performExport directly
      try {
        const token = localStorage.getItem('auth_token');
        console.log('🚀 DataDashboard performing export to:', API_ENDPOINTS.EXPORT_DATA);

        const response = await fetch(API_ENDPOINTS.EXPORT_DATA, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(exportData)
        });

        console.log('🚀 DataDashboard export response status:', response.status);

        if (!response.ok) {
          const errorText = await response.text();
          console.error('🚀 DataDashboard export error response:', errorText);
          throw new Error(`Export failed: ${response.status} ${errorText}`);
        }

        // Handle file download
        const blob = await response.blob();
        console.log('🚀 DataDashboard export blob size:', blob.size);
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        // Format: isim_YYYYMMDD
        const today = new Date();
        const dateStr = `${today.getFullYear()}${String(today.getMonth() + 1).padStart(2, '0')}${String(today.getDate()).padStart(2, '0')}`;
        const baseFilename = exportData.filename || collection || 'export';
        a.download = `${baseFilename}_${dateStr}.${exportData.format === 'excel' ? 'xlsx' : 'csv'}`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);

        console.log('🚀 DataDashboard export completed successfully');
      } catch (error) {
        console.error('🚀 DataDashboard export error:', error);
        throw error;
      }
    }
  };


  // Handle sort
  const handleSort = (key: string) => {
    // Prevent multiple clicks while sorting
    if (sortLoading) {
      console.log('🚫 SORT DEBUG - Already loading, ignoring click');
      return;
    }

    console.log('🔄 SORT DEBUG - Starting sort for:', key);

    let newSortConfig: { key: string; direction: 'asc' | 'desc' } | null = null;

    if (sortConfig?.key === key) {
      if (sortConfig.direction === 'asc') {
        newSortConfig = { key, direction: 'desc' as const };
      } else if (sortConfig.direction === 'desc') {
        newSortConfig = null; // Remove sort
      }
    } else {
      newSortConfig = { key, direction: 'asc' as const };
    }

    console.log('🔄 SORT DEBUG - New sort config:', newSortConfig);
    setSortConfig(newSortConfig);


    // Call backend API if onDataFetch is provided
    if (onDataFetch) {
      setSortLoading(true);
      console.log('🔄 SORT DEBUG - Sort loading set to true');
      // Set default sort key based on collection
      const defaultSortKey = collection === 'products' ? 'product_id' :
        collection === 'suppliers' ? 'global_id' :
          collection === 'companies' ? 'company_id' : 'name';

      const sortBy = newSortConfig ? newSortConfig.key : defaultSortKey;
      const sortOrder = newSortConfig ? newSortConfig.direction : 'asc';
      const currentPage = paginationInfo?.page || 1;
      const currentLimit = paginationInfo?.limit || itemsPerPage;

      console.log('🔄 SORT DEBUG - Calling onDataFetch with:', { sortBy, sortOrder, defaultSortKey });

      onDataFetch({
        page: currentPage,
        limit: currentLimit,
        search: searchTerm,
        sortBy,
        sortOrder
      });
    }
  };

  // Handle column filter
  const handleColumnFilter = (columnKey: string, value: string) => {
    // Clear existing timeout for this column
    if (debounceTimeouts[columnKey]) {
      clearTimeout(debounceTimeouts[columnKey]);
    }

    // Update local state immediately for UI responsiveness
    setColumnFilters(prev => ({
      ...prev,
      [columnKey]: value
    }));

    // Send column filter to backend with debounce
    if (onDataFetch) {
      const timeoutId = setTimeout(() => {
        const currentPage = paginationInfo?.page || 1;
        const currentLimit = paginationInfo?.limit || itemsPerPage;
        const currentSortBy = sortConfig?.key || 'company_id';
        const currentSortOrder = sortConfig?.direction || 'asc';

        // Create updated column filters with current state
        const updatedColumnFilters = {
          ...columnFilters,
          [columnKey]: value
        };

        onDataFetch({
          page: 1, // Reset to first page when filtering
          limit: currentLimit,
          search: searchTerm,
          sortBy: currentSortBy,
          sortOrder: currentSortOrder,
          columnFilters: updatedColumnFilters
        });

        // Clear timeout from state
        setDebounceTimeouts(prev => {
          const newTimeouts = { ...prev };
          delete newTimeouts[columnKey];
          return newTimeouts;
        });
      }, 500);

      // Store timeout ID
      setDebounceTimeouts(prev => ({
        ...prev,
        [columnKey]: timeoutId
      }));
    }
  };

  // Handle column drag start
  const handleColumnDragStart = (e: React.DragEvent, columnKey: string) => {
    setDraggedColumn(columnKey);
    e.dataTransfer.effectAllowed = 'move';
  };

  // Handle column drag over
  const handleColumnDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  // Handle column drop
  const handleColumnDrop = (e: React.DragEvent, targetColumnKey: string) => {
    e.preventDefault();

    if (!draggedColumn || draggedColumn === targetColumnKey) {
      return;
    }

    const newOrder = [...columnOrder];
    const draggedIndex = newOrder.indexOf(draggedColumn);
    const targetIndex = newOrder.indexOf(targetColumnKey);

    // Remove dragged column from its current position
    newOrder.splice(draggedIndex, 1);

    // Insert dragged column at target position
    newOrder.splice(targetIndex, 0, draggedColumn);

    setColumnOrder(newOrder);
    setDraggedColumn(null);
  };

  // Handle column drag end
  const handleColumnDragEnd = () => {
    setDraggedColumn(null);
  };

  // Handle column resize start
  const handleResizeStart = (e: React.MouseEvent, columnKey: string) => {
    e.stopPropagation(); // Prevent column drag and sort
    e.preventDefault();

    const th = (e.target as HTMLElement).closest('th');
    if (!th) {
      console.error('❌ RESIZE: Could not find th element');
      return;
    }

    console.log(`🔧 RESIZE START: Column ${columnKey}, Width: ${th.offsetWidth}px`);
    setResizingColumn(columnKey);
    setResizeStartX(e.clientX);
    setResizeStartWidth(th.offsetWidth);
  };

  // Handle column resize move
  useEffect(() => {
    if (!resizingColumn) return;

    console.log(`🎯 RESIZE ACTIVE: Column ${resizingColumn}`);

    const handleMouseMove = (e: MouseEvent) => {
      const diff = e.clientX - resizeStartX;
      const newWidth = Math.max(50, resizeStartWidth + diff); // Min width 50px

      setColumnWidths(prev => {
        const updated = {
          ...prev,
          [resizingColumn]: newWidth
        };
        console.log(`📏 RESIZE: ${resizingColumn} = ${newWidth}px, State:`, updated);
        return updated;
      });
    };

    const handleMouseUp = () => {
      console.log(`✅ RESIZE END: Column ${resizingColumn}`);
      setResizingColumn(null);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [resizingColumn, resizeStartX, resizeStartWidth]);

  // Handle column visibility toggle
  const handleColumnVisibilityToggle = (columnKey: string) => {
    setColumnVisibility(prev => ({
      ...prev,
      [columnKey]: !prev[columnKey]
    }));
  };

  // Reset column visibility to show all
  const resetColumnVisibility = () => {
    const allVisible = columns.reduce((acc, col) => ({ ...acc, [col.key]: true }), {});
    setColumnVisibility(allVisible);
  };

  // Handle add new item
  const handleAddNew = () => {
    // If onAdd prop is provided, use it (for custom add behavior)
    if (onAdd) {
      onAdd();
    }
    // For requests collection, navigate to marketplace instead of opening modal
    else if (collection === 'requests' && onNavigate) {
      onNavigate('/dashboard/user/create-request/marketplace');
    } else {
      setShowAddModal(true);
    }
  };

  // Handle close add modal
  const handleCloseAddModal = () => {
    setShowAddModal(false);
  };

  // Clear all filters
  const clearAllFilters = () => {
    // Clear all pending timeouts
    Object.values(debounceTimeouts).forEach(timeout => {
      if (timeout) clearTimeout(timeout);
    });
    setDebounceTimeouts({});

    setSearchTerm('');
    setColumnFilters({});

    // Clear filters in backend
    if (onDataFetch) {
      const currentPage = paginationInfo?.page || 1;
      const currentLimit = paginationInfo?.limit || itemsPerPage;
      const currentSortBy = sortConfig?.key || 'company_id';
      const currentSortOrder = sortConfig?.direction || 'asc';

      onDataFetch({
        page: 1, // Reset to first page when clearing filters
        limit: currentLimit,
        search: '',
        sortBy: currentSortBy,
        sortOrder: currentSortOrder,
        columnFilters: {}
      });
    }
  };

  // Render cell content
  const renderCellContent = (column: Column, value: any, row: any) => {
    if (column.render) {
      return column.render(value, row);
    }

    switch (column.type) {
      case 'date':
        if (!value) return 'N/A';
        try {
          return new Date(value).toLocaleDateString();
        } catch (error) {
          return 'Invalid Date';
        }
      case 'status':
        let statusValue = '';
        let statusClass = 'status-default';
        let displayValue = '';

        // Handle boolean values (like is_active)
        // false = pending, true = active
        if (typeof value === 'boolean') {
          if (value === false) {
            // is_active: false means pending
            statusValue = 'pending';
            displayValue = 'Pending';
            statusClass = 'status-pending';
          } else {
            statusValue = 'active';
            displayValue = 'Active';
            statusClass = 'status-active';
          }
        } else if (value && typeof value === 'string' && value.trim()) {
          statusValue = value.toLowerCase().trim();
          displayValue = value;

          // Handle different status types
          if (statusValue === 'yes' || statusValue === 'no') {
            statusClass = statusValue === 'yes' ? 'status-active' : 'status-inactive';
            displayValue = statusValue === 'yes' ? 'Yes' : 'No';
          } else if (statusValue === 'approved' || statusValue === 'pending' || statusValue === 'rejected') {
            statusClass = `status-${statusValue}`;
          } else if (statusValue === 'active' || statusValue === 'inactive' || statusValue === 'pending') {
            statusClass = `status-${statusValue}`;
          } else {
            // For other string values, try to match common patterns
            if (statusValue.includes('active') || statusValue.includes('demo')) {
              statusClass = 'status-active';
            } else if (statusValue.includes('inactive') || statusValue.includes('suspended')) {
              statusClass = 'status-inactive';
            } else if (statusValue.includes('pending')) {
              statusClass = 'status-pending';
            }
          }
        } else {
          // Default to "Pending" for empty/null/undefined values (new items start as pending)
          displayValue = 'Pending';
          statusClass = 'status-pending';
        }

        return (
          <span className={`status-badge ${statusClass}`}>
            {displayValue}
          </span>
        );
      case 'score':
        const score = parseFloat(value) || 0;
        return (
          <span className="score-value">
            {score.toFixed(1)}
          </span>
        );
      case 'categories':
        if (!value || (Array.isArray(value) && value.length === 0)) {
          return <span className="text-muted">No categories</span>;
        }

        // Parse category IDs - handle various formats
        let parsedCategoryIds: string[] = [];
        if (Array.isArray(value)) {
          parsedCategoryIds = value;
        } else if (typeof value === 'string') {
          // Check if it's a concatenated string of 8-digit IDs
          if (value.length > 8 && /^\d+$/.test(value)) {
            // Split into 8-digit chunks
            parsedCategoryIds = value.match(/.{1,8}/g) || [value];
          } else if (value.includes(',')) {
            // Comma-separated
            parsedCategoryIds = value.split(',').map(s => s.trim());
          } else {
            parsedCategoryIds = [value];
          }
        }

        const categoryIds = parsedCategoryIds;
        const columnWidth = columnWidths[column.key];

        // If column is wide enough (>300px), show all categories, otherwise show first 2
        const showAllCategories = columnWidth && columnWidth > 300;
        const categoriesToShow = showAllCategories ? categoryIds : categoryIds.slice(0, 2);

        return (
          <div className="categories-display">
            {categoriesToShow.map((categoryId: string, index: number) => (
              <span key={index} className="category-tag" title={getCategoryName(categoryId, 'en')}>
                {getCategoryName(categoryId, 'tr')}
              </span>
            ))}
            {!showAllCategories && categoryIds.length > 2 && (
              <span className="category-more">
                +{categoryIds.length - 2}
              </span>
            )}
          </div>
        );
      case 'companies':
        // Value is array of {company_id, company_name}
        if (!value || (Array.isArray(value) && value.length === 0)) {
          return <span className="text-muted">None</span>;
        }

        const companies = Array.isArray(value) ? value : [value];
        const companyColumnWidth = columnWidths[column.key];

        // If column is wide enough (>300px), show all companies, otherwise show first 2
        const showAllCompanies = companyColumnWidth && companyColumnWidth > 300;
        const companiesToShow = showAllCompanies ? companies : companies.slice(0, 2);

        return (
          <div className="companies-display">
            {companiesToShow.map((company: any, index: number) => (
              <span key={index} className="company-tag">
                {company.company_name || company}
              </span>
            ))}
            {!showAllCompanies && companies.length > 2 && (
              <span className="company-more">
                +{companies.length - 2}
              </span>
            )}
          </div>
        );
      case 'image':
        if (!value || !row.image_gridfs_id) {
          return <span className="no-image">No Image</span>;
        }
        const imageUrl = `${config.API_BASE_URL.replace('/api', '')}/api/gridfs/${row.image_gridfs_id}`;
        return (
          <img
            src={imageUrl}
            alt={row.name || 'Product'}
            className="product-image"
            onError={(e) => {
              e.currentTarget.style.display = 'none';
              e.currentTarget.nextElementSibling?.classList.remove('hidden');
            }}
          />
        );
      case 'price_range':
        if (!value || typeof value !== 'object') return 'N/A';
        const { min, max, currency = 'TRY' } = value;
        return (
          <span className="price-range">
            ₺{min?.toLocaleString()} - ₺{max?.toLocaleString()}
          </span>
        );
      case 'dropdown':
        if (!value) return 'N/A';
        return (
          <span className="dropdown-value">
            {value.charAt(0).toUpperCase() + value.slice(1)}
          </span>
        );
      case 'action':
        return (
          <div className="action-buttons">
            <button
              className="edit-btn"
              onClick={(e) => {
                console.log('🔍 DEBUG: Edit button clicked');
                e.stopPropagation();
                handleEditToggle();
              }}
            >
              {isEditing ? 'Cancel' : 'Edit'}
            </button>
            <button
              className="delete-btn"
              onClick={(e) => {
                e.stopPropagation();
                if (onDelete && row.id) {
                  onDelete(row.id);
                } else if (onDelete && row._id) {
                  onDelete(row._id);
                }
              }}
            >
              Delete
            </button>
          </div>
        );
      default:
        return value;
    }
  };

  // Create header actions
  const headerActions = customHeaderActions || (
    <HeaderActions
      {...createStandardHeaderActions({
        isEditing,
        onEditToggle: showEditButton ? handleEditToggle : () => { },
        onSaveChanges: handleSaveChanges,
        onExport: () => setShowExportModal(true),
        onDelete: showDeleteButton ? handleDelete : () => { },
        selectedItemsCount: selectedItemsCount,
        itemsPerPage,
        onItemsPerPageChange: handleItemsPerPageChange,
        onOpen: onOpen
          ? () => {
            if (selectedItems.length === 1) {
              onOpen(selectedItems[0]);
            }
          }
          : undefined,
        onFiltersToggle: () => setShowColumnToggle(!showColumnToggle),
        useIndividualComponents: true, // Use individual button components
        showEditButton,
        showDeleteButton,
        showExportButton,
        onAddNew: collection === 'requests' ? handleAddNew : undefined,
        showAddButton: collection === 'requests', // Always show in header for requests, showAddButton prop only controls search bar
        customButtons: propHeaderActions || []
      })}
    />
  );

  // Choose layout based on layoutType
  const LayoutComponent = layoutType === 'user' ? UserLayout : AdminLayout;

  // Memoize search and filter bar to prevent re-render during data loading
  const searchAndFilterBar = useMemo(() => {
    if (!showSearch && !showAddButton) return null;

    return (
      <div className="search-actions-bar">
        {showSearch && (
          <div className="search-field">
            <input
              type="text"
              placeholder={`Search ${title.toLowerCase()}...`}
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                // Debounce search to avoid too many API calls
                if (onDataFetch) {
                  const currentPage = paginationInfo?.page || 1;
                  const currentLimit = paginationInfo?.limit || itemsPerPage;
                  const currentSortBy = sortConfig?.key || 'company_id';
                  const currentSortOrder = sortConfig?.direction || 'asc';

                  // Simple debounce - you might want to use a proper debounce hook
                  setTimeout(() => {
                    onDataFetch({
                      page: 1, // Reset to first page when searching
                      limit: currentLimit,
                      search: e.target.value,
                      sortBy: currentSortBy,
                      sortOrder: currentSortOrder,
                      columnFilters: columnFilters
                    });
                  }, 500);
                }
              }}
              className="search-input"
            />
            {/* Clear All Filters Button - next to search bar */}
            {(searchTerm || Object.keys(columnFilters).some(key => columnFilters[key])) && (
              <button
                className="clear-filters-button"
                onClick={clearAllFilters}
                title="Clear all filters"
              >
                Clear Filters
              </button>
            )}
          </div>
        )}

        {showAddButton && (
          <button
            className="add-button"
            onClick={handleAddNew}
          >
            {addButtonLabel || `Add New ${collection === 'suppliers' ? 'Supplier' : collection === 'companies' ? 'Company' : collection.slice(0, -1).charAt(0).toUpperCase() + collection.slice(1, -1)}`}
          </button>
        )}
      </div>
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showSearch, showAddButton, searchTerm, columnFilters, title, collection, addButtonLabel]);

  // Calculate select all state - memoize only the calculation, not the JSX
  const allSelected = useMemo(() => {
    if (!showSelection || displayData.length === 0) return false;
    return selectedItems.length > 0 &&
      displayData.every(row => selectedItems.includes(row.id || row._id));
  }, [showSelection, selectedItems, displayData]);

  // Filter row - memoized to prevent re-render when data changes
  // Input values are controlled via columnFilters state, React updates them efficiently
  const filterRow = useMemo(() => {
    return (
      <tr className="filter-row">
        {showSelection && (
          <th className="selection-column"></th>
        )}
        {expandableRows && (
          <th className="expand-column"></th>
        )}
        {/* Action column first (no filter input) */}
        {onRowAction && rowActionLabel && (
          <th className="row-action-header"></th>
        )}
        {columnOrder.map(columnKey => {
          const column = columns.find(col => col.key === columnKey);
          if (!column || !columnVisibility[columnKey]) return null;

          // Only show filter input if column is filterable (default: true for backward compatibility)
          const isFilterable = column.filterable !== false;

          return (
            <th key={`filter-${column.key}`} className={`filter-${column.key}`}>
              {isFilterable ? (
                <input
                  type="text"
                  placeholder={`Filter ${column.title}...`}
                  value={columnFilters[column.key] || ''}
                  onChange={(e) => handleColumnFilter(column.key, e.target.value)}
                  className="column-filter-input"
                  onClick={(e) => e.stopPropagation()}
                  autoComplete="off"
                />
              ) : (
                <span style={{ display: 'block', height: '32px' }}></span>
              )}
            </th>
          );
        })}
      </tr>
    );
    // Include columnFilters in deps so input values update, but structure stays stable
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showSelection, columnOrder.length, columns.length, columnVisibility, columnFilters, onRowAction, rowActionLabel]);

  // Memoize table header to prevent re-render during data loading
  // Only re-render when column structure changes, not when data changes
  const tableHeader = useMemo(() => {
    return (
      <thead>
        <tr>
          {showSelection && (
            <th className="selection-column">
              <input
                type="checkbox"
                checked={allSelected}
                onChange={handleSelectAll}
              />
            </th>
          )}
          {expandableRows && !hideExpandColumn && (
            <th className="expand-column" style={{ width: '50px' }}></th>
          )}
          {/* Action column first */}
          {onRowAction && rowActionLabel && (
            <th className="row-action-header">{rowActionLabel}</th>
          )}
          {columnOrder.map(columnKey => {
            const column = columns.find(col => col.key === columnKey);
            if (!column || !columnVisibility[columnKey]) return null;

            return (
              <th
                key={column.key}
                className={`column-${column.key} ${column.sortable ? 'sortable' : ''} ${draggedColumn === column.key ? 'dragging' : ''} ${sortLoading && sortConfig?.key === column.key ? 'sort-loading' : ''}`}
                style={{
                  width: columnWidths[column.key] ? `${columnWidths[column.key]}px` : (column.width || 'auto'),
                  minWidth: columnWidths[column.key] ? `${columnWidths[column.key]}px` : (column.width || 'auto'),
                  maxWidth: columnWidths[column.key] ? `${columnWidths[column.key]}px` : (column.width || 'auto'),
                  position: 'relative',
                  cursor: sortLoading ? 'wait' : (column.sortable ? 'pointer' : 'default'),
                  opacity: sortLoading && sortConfig?.key === column.key ? 0.7 : 1
                }}
                onClick={(e) => {
                  // Don't sort if clicking on resize handle or if already loading
                  if ((e.target as HTMLElement).classList.contains('column-resize-handle')) {
                    console.log('🚫 SORT DEBUG - Clicked on resize handle, ignoring');
                    return;
                  }
                  if (sortLoading) {
                    console.log('🚫 SORT DEBUG - Sort loading is true, ignoring click');
                    return;
                  }
                  if (column.sortable) {
                    console.log('🔄 SORT DEBUG - Clicking on sortable column:', column.key);
                    handleSort(column.key);
                  }
                }}
                draggable={true}
                onDragStart={(e) => handleColumnDragStart(e, column.key)}
                onDragOver={handleColumnDragOver}
                onDrop={(e) => handleColumnDrop(e, column.key)}
                onDragEnd={handleColumnDragEnd}
              >
                <div className="column-header-content">
                  <span className="column-title">{column.title}</span>
                  {column.sortable && sortConfig?.key === column.key && (
                    <span className="sort-indicator">
                      {sortLoading ? '⟳' : (sortConfig.direction === 'asc' ? '↑' : '↓')}
                    </span>
                  )}
                </div>
                {/* Resize handle */}
                <div
                  className="column-resize-handle"
                  onMouseDown={(e) => handleResizeStart(e, column.key)}
                  onClick={(e) => e.stopPropagation()}
                />
              </th>
            );
          })}
        </tr>
        {/* Filter row - aynı thead içinde */}
        {filterRow}
      </thead>
    );
    // Only re-render when column structure or selection state changes
    // Note: filterRow is now inside the same thead
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showSelection, allSelected, columnOrder.length, columns.length, columnVisibility, columnWidths, draggedColumn, sortLoading, sortConfig?.key, sortConfig?.direction, handleSelectAll, onRowAction, rowActionLabel, filterRow]);

  const layoutContent = (
    <>
      {/* Optional header content */}
      {headerContent && (
        <div className="data-dashboard-header-content">
          {headerContent}
        </div>
      )}

      <div className="data-dashboard-content">
        {/* Search and Actions Bar - Memoized to prevent re-render during data loading */}
        {searchAndFilterBar}

        {/* Data Table */}
        <div className="data-table-container">
          {error ? (
            <div className="error-state">
              <div className="error-icon">⚠️</div>
              <p>Error loading {title.toLowerCase()}</p>
              <p className="error-message">{error}</p>
            </div>
          ) : (
            <div className="table-container" style={{ position: 'relative' }}>
              {sortLoading && (
                <div className="table-loading-overlay">
                  <span>Sorting...</span>
                </div>
              )}
              <table className="data-table" style={{
                pointerEvents: sortLoading ? 'none' : 'auto',
                opacity: sortLoading ? 0.6 : 1
              }}>
                {tableHeader}
                <tbody>
                  {isLoading ? (
                    <tr>
                      <td
                        colSpan={columnOrder.filter(key => columnVisibility[key]).length + (showSelection ? 1 : 0) + (onRowAction ? 1 : 0)}
                        className="loading-cell"
                        style={{
                          textAlign: 'center',
                          padding: '60px 20px',
                          backgroundColor: 'transparent',
                          whiteSpace: 'normal',
                          overflow: 'visible',
                          width: '100%',
                          display: 'table-cell'
                        }}
                      >
                        <div
                          className="loading-state-inline"
                          style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '16px',
                            width: '100%',
                            margin: '0 auto',
                            textAlign: 'center'
                          }}
                        >
                          <div
                            className="loading-spinner"
                            style={{
                              width: '24px',
                              height: '24px',
                              border: '3px solid transparent',
                              borderTop: '3px solid #337AB7',
                              borderRadius: '50%',
                              animation: 'spin 1s linear infinite',
                              display: 'block',
                              margin: '0 auto',
                              background: 'transparent',
                              flexShrink: 0
                            }}
                          ></div>
                          <p style={{ color: '#374151', margin: 0, fontSize: '16px', fontWeight: 500, textAlign: 'center' }}>Loading {title.toLowerCase()}...</p>
                        </div>
                      </td>
                    </tr>
                  ) : displayData.length === 0 ? (
                    <tr>
                      <td colSpan={columnOrder.filter(key => columnVisibility[key]).length + (showSelection ? 1 : 0) + (onRowAction ? 1 : 0)} className="empty-table-cell">
                        <div className="empty-state">
                          <p>No {title.toLowerCase()} found</p>
                          <p className="empty-state-hint">Use the "Add New" button above to create your first item.</p>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    <>
                      {/* Spacer Row for visual separation */}
                      <tr className="table-spacer-row" aria-hidden="true">
                        <td
                          colSpan={columnOrder.filter(key => columnVisibility[key]).length + (showSelection ? 1 : 0) + (onRowAction ? 1 : 0) + (expandableRows ? 1 : 0)}
                          style={{ padding: 0, border: 'none', height: '6px', background: 'transparent' }}
                        />
                      </tr>
                      {displayData.map((row, index) => {
                        const rowClassNames = ['data-table-row'];
                        if (row._isStale) {
                          rowClassNames.push('data-table-row--stale');
                        }
                        if (getRowClassName) {
                          const customClassName = getRowClassName(row);
                          if (customClassName) {
                            rowClassNames.push(customClassName);
                          }
                        }
                        const rowId = row.id || row._id;
                        const isExpanded = expandableRows && expandedRows?.has(rowId);

                        return (
                          <React.Fragment key={rowId || index}>
                            <tr
                              className={rowClassNames.join(' ')}
                              onClick={(e) => {
                                // Don't trigger row click if clicking on checkbox, action button, or expand icon
                                const target = e.target as HTMLElement;
                                if (target.tagName === 'INPUT' || target.tagName === 'BUTTON' || target.closest('button') || target.closest('input') || target.closest('.expand-icon')) {
                                  return;
                                }
                                if (expandableRows && onRowExpand) {
                                  onRowExpand(rowId);
                                  return;
                                }
                                if (onRowClick) {
                                  onRowClick(row);
                                } else if (showSelection && !isEditing) {
                                  handleSelectItem(rowId);
                                }
                              }}
                              style={{ cursor: (expandableRows || onRowClick || (showSelection && !isEditing)) ? 'pointer' : 'default' }}
                            >
                              {showSelection && (
                                <td className="selection-column" onClick={(e) => e.stopPropagation()}>
                                  <input
                                    type="checkbox"
                                    checked={selectedItems.includes(row.id || row._id)}
                                    disabled={isEditing}
                                    onChange={(e) => {
                                      e.stopPropagation();
                                      if (!isEditing) {
                                        handleSelectItem(row.id || row._id);
                                      }
                                    }}
                                    onClick={(e) => e.stopPropagation()}
                                  />
                                </td>
                              )}
                              {expandableRows && !hideExpandColumn && (
                                <td
                                  className="expand-column"
                                  style={{ width: '50px', padding: '12px', textAlign: 'center' }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (onRowExpand) {
                                      onRowExpand(rowId);
                                    }
                                  }}
                                >
                                  <span
                                    className="expand-icon"
                                    style={{
                                      display: 'inline-block',
                                      width: '20px',
                                      textAlign: 'center',
                                      fontWeight: 'bold',
                                      color: '#666',
                                      cursor: 'pointer',
                                      userSelect: 'none'
                                    }}
                                  >
                                    {isExpanded ? '▼' : '▶'}
                                  </span>
                                </td>
                              )}
                              {/* Row action button first */}
                              {onRowAction && rowActionLabel && (
                                <td className="row-action-column">
                                  <button
                                    className="row-action-button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      onRowAction(row);
                                    }}
                                  >
                                    {rowActionLabel}
                                  </button>
                                </td>
                              )}
                              {columnOrder.map(columnKey => {
                                const column = columns.find(col => col.key === columnKey);
                                if (!column || !columnVisibility[columnKey]) return null;

                                return (
                                  <td
                                    key={column.key}
                                    className={`column-${column.key}`}
                                    style={{
                                      width: columnWidths[column.key] ? `${columnWidths[column.key]}px` : (column.width || 'auto'),
                                      minWidth: columnWidths[column.key] ? `${columnWidths[column.key]}px` : (column.width || 'auto'),
                                      maxWidth: columnWidths[column.key] ? `${columnWidths[column.key]}px` : (column.width || 'auto')
                                    }}
                                  >
                                    {isEditing && column.type !== 'action' ? (
                                      column.type === 'status' && column.editable ? (
                                        (() => {
                                          const currentValue = pendingChanges[`${row.id || row._id}_${column.key}`] ?? row[column.key];
                                          const originalValue = row[column.key];

                                          // Check if this is approval_status with pending/approved values
                                          const isApprovalStatus = column.key === 'approval_status';
                                          const currentValueStrForApproval = typeof currentValue === 'string' ? currentValue.toLowerCase() : '';

                                          // If approval_status, use dropdown
                                          if (isApprovalStatus && (currentValueStrForApproval === 'pending' || currentValueStrForApproval === 'approved' || currentValueStrForApproval === 'rejected' || !currentValue)) {
                                            return (
                                              <select
                                                value={currentValue || 'pending'}
                                                onChange={(e) => {
                                                  handleItemEdit(row.id || row._id, column.key, e.target.value, row);
                                                }}
                                                onClick={(e) => e.stopPropagation()}
                                                style={{
                                                  width: '100%',
                                                  padding: '4px 8px',
                                                  borderRadius: '4px',
                                                  border: '1px solid #ccc',
                                                  fontSize: '13px',
                                                  cursor: 'pointer'
                                                }}
                                              >
                                                <option value="pending">Pending</option>
                                                <option value="approved">Approved</option>
                                                <option value="rejected">Rejected</option>
                                              </select>
                                            );
                                          }

                                          // For status fields (not approval_status), use 3-way switch: active/inactive/pending
                                          // Determine current status
                                          const statusValueStr = typeof currentValue === 'string' ? currentValue.toLowerCase().trim() : '';
                                          const isBoolean = typeof currentValue === 'boolean';

                                          // Map to status string
                                          let currentStatus: 'active' | 'inactive' | 'pending' = 'pending';
                                          if (isBoolean) {
                                            // For boolean: false = pending, true = active
                                            currentStatus = currentValue ? 'active' : 'pending';
                                          } else if (statusValueStr === 'active' || statusValueStr === 'yes' || statusValueStr === 'true') {
                                            currentStatus = 'active';
                                          } else if (statusValueStr === 'inactive' || statusValueStr === 'no' || statusValueStr === 'false') {
                                            currentStatus = 'inactive';
                                          } else if (statusValueStr === 'pending') {
                                            currentStatus = 'pending';
                                          }

                                          // Determine original value type to preserve format
                                          const isOriginalString = typeof originalValue === 'string';
                                          const isOriginalYesNo = isOriginalString && (originalValue.toLowerCase() === 'yes' || originalValue.toLowerCase() === 'no');

                                          // Cycle through: pending -> active -> inactive -> pending
                                          const getNextStatus = (current: 'active' | 'inactive' | 'pending'): 'active' | 'inactive' | 'pending' => {
                                            if (current === 'pending') return 'active';
                                            if (current === 'active') return 'inactive';
                                            return 'pending';
                                          };

                                          const getStatusColor = (status: 'active' | 'inactive' | 'pending') => {
                                            if (status === 'active') return '#10b981';
                                            if (status === 'inactive') return '#ef4444';
                                            return '#f59e0b'; // Orange for pending
                                          };

                                          const getStatusLabel = (status: 'active' | 'inactive' | 'pending') => {
                                            if (status === 'active') return 'Active';
                                            if (status === 'inactive') return 'Inactive';
                                            return 'Pending';
                                          };

                                          return (
                                            <span
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                // Cycle to next status
                                                const nextStatus = getNextStatus(currentStatus);

                                                // Preserve original format
                                                let valueToSave: any;
                                                if (isOriginalYesNo) {
                                                  valueToSave = nextStatus === 'active' ? 'yes' : 'no';
                                                } else if (isOriginalString && !isOriginalYesNo) {
                                                  valueToSave = nextStatus;
                                                } else {
                                                  valueToSave = nextStatus;
                                                }

                                                handleItemEdit(row.id || row._id, column.key, valueToSave, row);
                                              }}
                                              className={`status-badge status-${currentStatus}`}
                                              style={{
                                                cursor: 'pointer',
                                                transition: 'all 0.2s',
                                                userSelect: 'none',
                                                // Override specific properties to ensure consistency if needed, 
                                                // otherwise let CSS class handle it
                                                display: 'inline-flex'
                                              }}
                                              title="Click to toggle status"
                                            >
                                              {getStatusLabel(currentStatus)}
                                            </span>
                                          );
                                        })()
                                      ) : column.type === 'categories' ? (
                                        <CategorySelector
                                          value={Array.isArray(pendingChanges[`${row.id || row._id}_${column.key}`] ?? row[column.key])
                                            ? pendingChanges[`${row.id || row._id}_${column.key}`] ?? row[column.key]
                                            : [pendingChanges[`${row.id || row._id}_${column.key}`] ?? row[column.key]].filter(Boolean)}
                                          onChange={(categoryIds) => handleItemEdit(row.id || row._id, column.key, categoryIds)}
                                          language="tr"
                                          multiple={true}
                                          className="edit-category-selector"
                                        />
                                      ) : column.type === 'companies' ? (
                                        <CompanySelector
                                          value={Array.isArray(pendingChanges[`${row.id || row._id}_${column.key}`] ?? row[column.key])
                                            ? pendingChanges[`${row.id || row._id}_${column.key}`] ?? row[column.key]
                                            : (pendingChanges[`${row.id || row._id}_${column.key}`] ?? row[column.key] ? [pendingChanges[`${row.id || row._id}_${column.key}`] ?? row[column.key]] : [])}
                                          onChange={(companies) => handleItemEdit(row.id || row._id, column.key, companies)}
                                          multiple={true}
                                          className="edit-company-selector"
                                        />
                                      ) : column.type === 'dropdown' && column.editable ? (
                                        <select
                                          value={
                                            pendingChanges[`${row.id || row._id}_${column.key}`] ??
                                            row[column.key] ?? ''
                                          }
                                          onChange={(e) => handleItemEdit(row.id || row._id, column.key, e.target.value, row)}
                                          className="edit-input"
                                        >
                                          {column.options && Array.isArray(column.options) && column.options.map((option: string | { value: string; label: string }) => {
                                            // Handle both string[] and {value, label}[] formats
                                            const optionValue = typeof option === 'string' ? option : option.value;
                                            const optionLabel = typeof option === 'string' ? option.charAt(0).toUpperCase() + option.slice(1) : option.label;
                                            return (
                                              <option key={optionValue} value={optionValue}>
                                                {optionLabel}
                                              </option>
                                            );
                                          })}
                                        </select>
                                      ) : column.options && column.editable ? (
                                        <>
                                          <input
                                            list={`datalist-${row.id || row._id}-${column.key}`}
                                            value={
                                              (() => {
                                                const currentValue = pendingChanges[`${row.id || row._id}_${column.key}`] ?? row[column.key];
                                                if (!column.options || column.options.length === 0) return currentValue || '';

                                                // For supplier_name and product_name, row[column.key] already contains the display name
                                                // No need to lookup in options
                                                if (column.key === 'supplier_name' || column.key === 'product_name') {
                                                  return currentValue || '';
                                                }

                                                // Check if options is string[] or {value, label}[]
                                                if (typeof column.options[0] === 'string') {
                                                  return currentValue || '';
                                                }
                                                const optionsArray = column.options as Array<{ value: string; label: string }>;
                                                const option = optionsArray.find((opt: { value: string; label: string }) => opt.value === currentValue);
                                                return option ? option.label : currentValue || '';
                                              })()
                                            }
                                            onChange={(e) => {
                                              if (!column.options || column.options.length === 0) {
                                                handleItemEdit(row.id || row._id, column.key, e.target.value);
                                                return;
                                              }
                                              // Check if options is string[] or {value, label}[]
                                              if (typeof column.options[0] === 'string') {
                                                handleItemEdit(row.id || row._id, column.key, e.target.value);
                                                return;
                                              }
                                              const optionsArray = column.options as Array<{ value: string; label: string }>;
                                              // Find the option that matches the input label
                                              const matchedOption = optionsArray.find((opt: { value: string; label: string }) =>
                                                opt.label.toLowerCase().includes(e.target.value.toLowerCase())
                                              );
                                              // If exact match found, use the value, otherwise store the typed text
                                              const selectedValue = optionsArray.find((opt: { value: string; label: string }) => opt.label === e.target.value)?.value || e.target.value;
                                              handleItemEdit(row.id || row._id, column.key, selectedValue);
                                            }}
                                            className="edit-input"
                                            style={{ width: '100%', padding: '4px' }}
                                            placeholder="Type to search..."
                                          />
                                          <datalist id={`datalist-${row.id || row._id}-${column.key}`}>
                                            {column.options && column.options.length > 0 && typeof column.options[0] === 'object' && (column.options as Array<{ value: string; label: string }>).map((option: { value: string; label: string }) => (
                                              <option key={option.value} value={option.label} />
                                            ))}
                                          </datalist>
                                        </>
                                      ) : column.editable ? (
                                        <input
                                          type="text"
                                          value={
                                            pendingChanges[`${row.id || row._id}_${column.key}`] ??
                                            (column.render ? column.render(row[column.key], row) : row[column.key]) ?? ''
                                          }
                                          onChange={(e) => handleItemEdit(row.id || row._id, column.key, e.target.value, row)}
                                          className="edit-input"
                                        />
                                      ) : (
                                        renderCellContent(column, row[column.key], row)
                                      )
                                    ) : (
                                      renderCellContent(column, row[column.key], row)
                                    )}
                                  </td>
                                );
                              })}
                            </tr>
                            {expandableRows && isExpanded && renderExpandedContent && (
                              <tr>
                                <td
                                  colSpan={columnOrder.filter(key => columnVisibility[key]).length + (showSelection ? 1 : 0) + (onRowAction ? 1 : 0) + (expandableRows ? 1 : 0)}
                                  style={{
                                    padding: '0',
                                    backgroundColor: '#f9f9f9',
                                    borderBottom: '1px solid #eee'
                                  }}
                                >
                                  <div style={{ padding: '8px 12px' }}>
                                    {renderExpandedContent(row)}
                                  </div>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Pagination Controls */}
        {paginationInfo && paginationInfo.pages > 1 && (
          <div className="pagination-controls">
            <div className="pagination-info">
              Showing {((paginationInfo.page - 1) * paginationInfo.limit) + 1} to {Math.min(paginationInfo.page * paginationInfo.limit, paginationInfo.total)} of {paginationInfo.total} entries
            </div>

            <div className="pagination-buttons">
              {/* Previous Button */}
              <button
                className="pagination-btn"
                onClick={() => paginationInfo.has_prev && onDataFetch?.({
                  page: paginationInfo.page - 1,
                  limit: paginationInfo.limit,
                  search: searchTerm,
                  sortBy: sortConfig?.key || 'name',
                  sortOrder: sortConfig?.direction || 'asc',
                  columnFilters: columnFilters
                })}
                disabled={!paginationInfo.has_prev}
              >
                Previous
              </button>

              {/* Page Numbers */}
              {Array.from({ length: Math.min(paginationInfo.pages, 5) }, (_, i) => {
                let pageNum: number;
                if (paginationInfo.pages <= 5) {
                  pageNum = i + 1;
                } else if (paginationInfo.page <= 3) {
                  pageNum = i + 1;
                } else if (paginationInfo.page >= paginationInfo.pages - 2) {
                  pageNum = paginationInfo.pages - 4 + i;
                } else {
                  pageNum = paginationInfo.page - 2 + i;
                }

                return (
                  <button
                    key={pageNum}
                    className={`pagination-btn ${paginationInfo.page === pageNum ? 'active' : ''}`}
                    onClick={() => onDataFetch?.({
                      page: pageNum,
                      limit: paginationInfo.limit,
                      search: searchTerm,
                      sortBy: sortConfig?.key || 'name',
                      sortOrder: sortConfig?.direction || 'asc',
                      columnFilters: columnFilters
                    })}
                  >
                    {pageNum}
                  </button>
                );
              })}

              {/* Next Button */}
              <button
                className="pagination-btn"
                onClick={() => paginationInfo.has_next && onDataFetch?.({
                  page: paginationInfo.page + 1,
                  limit: paginationInfo.limit,
                  search: searchTerm,
                  sortBy: sortConfig?.key || 'name',
                  sortOrder: sortConfig?.direction || 'asc',
                  columnFilters: columnFilters
                })}
                disabled={!paginationInfo.has_next}
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );

  // If noLayout is true, render only content without layout wrapper
  if (noLayout) {
    return (
      <div className={`data-dashboard ${className}`}>
        {layoutContent}
        {/* Column Toggle Modal - also render when noLayout */}
        {showColumnToggle && (
          <div className="admin-v2-column-toggle-modal-overlay" onClick={() => setShowColumnToggle(false)}>
            <div className="admin-v2-column-toggle-modal" onClick={(e) => e.stopPropagation()}>
              <div className="admin-v2-modal-header">
                <h3 className="admin-v2-modal-title">Column Visibility</h3>
                <button
                  className="admin-v2-modal-close-button"
                  onClick={() => setShowColumnToggle(false)}
                >
                  ×
                </button>
              </div>

              <div className="admin-v2-modal-content">
                <div className="admin-v2-column-list">
                  {columns.map(column => (
                    <div key={column.key} className="admin-v2-column-item">
                      <label className="admin-v2-column-checkbox">
                        <input
                          type="checkbox"
                          className="admin-v2-column-checkbox-input"
                          checked={columnVisibility[column.key] || false}
                          onChange={() => handleColumnVisibilityToggle(column.key)}
                        />
                        <span className="admin-v2-column-checkbox-title">{column.title}</span>
                      </label>
                    </div>
                  ))}
                </div>

                <div className="admin-v2-modal-actions">
                  <button
                    className="admin-v2-modal-reset-button"
                    onClick={resetColumnVisibility}
                  >
                    Show All
                  </button>
                  <button
                    className="admin-v2-modal-close-modal-button"
                    onClick={() => setShowColumnToggle(false)}
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={`data-dashboard ${className}`}>
      {layoutType === 'admin' ? (
        <AdminLayout
          user={user}
          currentRoute={currentRoute}
          onNavigate={onNavigate}
          onLogout={onLogout}
          pageTitle={title}
          pageDescription={pageDescription || title}
          headerActions={headerActions}
          navigationActions={navigationActions}
          secondarySidebar={secondarySidebar}
          getDynamicSubItems={getDynamicSubItems}
        >
          {layoutContent}
        </AdminLayout>
      ) : (
        <UserLayout
          user={user}
          currentRoute={currentRoute}
          onNavigate={onNavigate}
          onLogout={onLogout}
          pageTitle={title}
          pageDescription={pageDescription || title}
          headerActions={headerActions}
          navigationActions={navigationActions}
          cartButton={cartButton}
          cartItemCount={cartItemCount}
          secondarySidebar={secondarySidebar}
          getDynamicSubItems={getDynamicSubItems}
        >
          {layoutContent}
        </UserLayout>
      )}

      {/* Column Toggle Modal */}
      {showColumnToggle && (
        <div className="admin-v2-column-toggle-modal-overlay" onClick={() => setShowColumnToggle(false)}>
          <div className="admin-v2-column-toggle-modal" onClick={(e) => e.stopPropagation()}>
            <div className="admin-v2-modal-header">
              <h3 className="admin-v2-modal-title">Column Visibility</h3>
              <button
                className="admin-v2-modal-close-button"
                onClick={() => setShowColumnToggle(false)}
              >
                ×
              </button>
            </div>

            <div className="admin-v2-modal-content">
              <div className="admin-v2-column-list">
                {columns.map(column => (
                  <div key={column.key} className="admin-v2-column-item">
                    <label className="admin-v2-column-checkbox">
                      <input
                        type="checkbox"
                        className="admin-v2-column-checkbox-input"
                        checked={columnVisibility[column.key] || false}
                        onChange={() => handleColumnVisibilityToggle(column.key)}
                      />
                      <span className="admin-v2-column-checkbox-title">{column.title}</span>
                    </label>
                  </div>
                ))}
              </div>

              <div className="admin-v2-modal-actions">
                <button
                  className="admin-v2-modal-reset-button"
                  onClick={resetColumnVisibility}
                >
                  Show All
                </button>
                <button
                  className="admin-v2-modal-close-modal-button"
                  onClick={() => setShowColumnToggle(false)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Modal */}
      {/* Render appropriate modal based on collection */}
      {collection === 'suppliers' && (
        <AddSupplierModal
          isOpen={showAddModal}
          onClose={handleCloseAddModal}
          onSuccess={() => {
            setShowAddModal(false);
            // Refresh data if onDataFetch is available
            if (onDataFetch) {
              const currentPage = paginationInfo?.page || 1;
              const currentLimit = paginationInfo?.limit || itemsPerPage;
              const currentSortBy = sortConfig?.key || 'supplier_id';
              const currentSortOrder = sortConfig?.direction || 'asc';

              onDataFetch({
                page: currentPage,
                limit: currentLimit,
                search: searchTerm,
                sortBy: currentSortBy,
                sortOrder: currentSortOrder
              });
            }
          }}
        />
      )}

      {AddModal && (
        <AddModal
          isOpen={showAddModal}
          onClose={handleCloseAddModal}
          onSuccess={() => {
            setShowAddModal(false);
            // Refresh data if onDataFetch is available
            if (onDataFetch) {
              const currentPage = paginationInfo?.page || 1;
              const currentLimit = paginationInfo?.limit || itemsPerPage;
              const currentSortBy = sortConfig?.key || 'company_id';
              const currentSortOrder = sortConfig?.direction || 'asc';

              onDataFetch({
                page: currentPage,
                limit: currentLimit,
                search: searchTerm,
                sortBy: currentSortBy,
                sortOrder: currentSortOrder
              });
            }
          }}
        />
      )}

      {/* Export Modal */}
      <ExportModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        collection={collection}
        collectionDisplayName={title}
        selectedItems={selectedItems.length > 0
          ? data.filter(item => selectedItems.includes(item.id || item._id))
          : data
        }
        onExport={handleExport}
        customFields={customExportFields}
      />
    </div>
  );
};

export default DataDashboard;
